import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Check, Mail, Phone, MessageSquare, Handshake, Clapperboard, Paperclip, Users } from "lucide-react";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import ConfirmContactDialog from "@/components/crm/ConfirmContactDialog";
import { statusOf, formatCountdown, formatDate, fullName } from "@/lib/crm";

const KIND_ICON = {
  email: Mail, phone: Phone, message: MessageSquare,
  meeting: Handshake, casting: Clapperboard, materials: Paperclip,
};

const DOT = { red: "bg-rose-500", yellow: "bg-amber-400", green: "bg-emerald-500" };

export default function CRMContact() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);

  const [contact, setContact] = useState(null);
  const [auditions, setAuditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    try {
      const [r, aRes] = await Promise.all([
        api.get(`/crm/contacts/${id}`),
        api.get(`/crm/contacts/${id}/auditions`).catch(() => ({ data: [] })),
      ]);
      setContact(r.data);
      setAuditions(aRes.data || []);
    } catch {
      toast.error("Not found");
      nav("/crm");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const del = async () => {
    if (!window.confirm(t("crm.confirm.delete"))) return;
    try {
      await api.delete(`/crm/contacts/${id}`);
      toast.success(t("crm.deleted"));
      nav("/crm");
    } catch { toast.error(t("crm.delete_fail")); }
  };

  if (loading) {
    return <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>;
  }
  if (!contact) return null;

  const s = statusOf(contact.followup_at);

  return (
    <div data-testid="crm-contact-page" className="space-y-8 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/crm" className="ghost-btn text-xs" data-testid="crm-back-btn">
          <ArrowLeft size={12} /> {t("crm.action.back")}
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setConfirmOpen(true)}
            data-testid="crm-profile-confirm-btn"
            className="pill-btn"
          >
            <Check size={14} /> {t("crm.action.confirm")}
          </button>
          <button
            onClick={() => setFormOpen(true)}
            data-testid="crm-profile-edit-btn"
            className="ghost-btn"
          >
            <Pencil size={14} /> {t("crm.action.edit")}
          </button>
          <button
            onClick={del}
            data-testid="crm-profile-delete-btn"
            className="ghost-btn hover:!text-rose-400 hover:!border-rose-500/50"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div>
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
          <Users size={12} /> {t(`crm.type.${contact.type}`)}
        </div>
        <h1 className="serif text-5xl text-stone-100 mt-2">{fullName(contact)}</h1>
        {contact.company && (
          <div className="mono text-xs text-stone-500 tracking-widest uppercase mt-1">{contact.company}</div>
        )}
      </div>

      {/* Overview grid */}
      <div className="paper p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <div className="label-cap mb-1">{t("crm.form.email")}</div>
          {contact.email ? (
            <a href={`mailto:${contact.email}`} className="text-stone-100 hover:text-amber-400 flex items-center gap-2">
              <Mail size={12} /> {contact.email}
            </a>
          ) : <div className="text-stone-500">—</div>}
        </div>
        <div>
          <div className="label-cap mb-1">{t("crm.form.phone")}</div>
          {contact.phone ? (
            <a href={`tel:${contact.phone}`} className="text-stone-100 hover:text-amber-400 flex items-center gap-2">
              <Phone size={12} /> {contact.phone}
            </a>
          ) : <div className="text-stone-500">—</div>}
        </div>
        <div className="md:col-span-2">
          <div className="label-cap mb-1">{t("crm.form.project")}</div>
          <div className="text-stone-200">{contact.project_context || "—"}</div>
        </div>
        <div className="md:col-span-2">
          <div className="label-cap mb-1">{t("crm.form.notes")}</div>
          <div className="text-stone-300 whitespace-pre-line">{contact.notes || "—"}</div>
        </div>
        <div>
          <div className="label-cap mb-1">{t("crm.col.last")}</div>
          <div className="text-stone-200">{formatDate(contact.last_contact_at, lang)}</div>
        </div>
        <div>
          <div className="label-cap mb-1">{t("crm.col.followup")}</div>
          <div className="text-stone-200 flex items-center gap-2">
            {formatDate(contact.followup_at, lang)}
            <span className={`inline-flex items-center gap-1.5 text-xs`}>
              <span className={`inline-block w-2 h-2 rounded-full ${DOT[s.color]}`} />
              <span className="text-stone-400">{formatCountdown(contact.followup_at, t)}</span>
            </span>
          </div>
        </div>
        <div>
          <div className="label-cap mb-1">{t("crm.profile.created")}</div>
          <div className="text-stone-400 text-sm">{formatDate(contact.created_at, lang)}</div>
        </div>
      </div>

      {/* Audition history (only meaningful for casting directors) */}
      {contact.type === "casting_director" && (
        <div>
          <div className="label-cap mb-3">{t("crm.profile.auditions")}</div>
          {auditions.length === 0 ? (
            <div className="paper p-6 text-center text-stone-500 text-sm">
              {t("crm.profile.no_auditions")}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="crm-audition-history">
              {auditions.map((a) => (
                <li key={a.audition_id}>
                  <Link
                    to={`/crm/auditions/${a.audition_id}`}
                    className="paper !p-4 flex items-center justify-between gap-3 hover:border-amber-500/40 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="serif text-lg text-stone-100 truncate">{a.project}</div>
                      <div className="text-xs text-stone-400 mt-0.5 space-x-2 truncate">
                        {a.role && <span>{a.role}</span>}
                        <span className="text-stone-500">·</span>
                        <span>{t(`aud.type.${a.audition_type}`)}</span>
                      </div>
                      {a.feedback && (
                        <div className="text-xs text-stone-500 italic truncate mt-1">{a.feedback}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                        {formatDate(a.created_at, lang)}
                      </span>
                      <span className="mono text-[10px] text-amber-500 uppercase tracking-widest">
                        {t(`aud.status.${a.status}`)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* History */}
      <div>
        <div className="label-cap mb-3">{t("crm.profile.history")}</div>
        {(!contact.history || contact.history.length === 0) ? (
          <div className="paper p-6 text-center text-stone-500 text-sm">
            {t("crm.profile.no_history")}
          </div>
        ) : (
          <ol className="relative border-l border-stone-800 pl-6 space-y-5" data-testid="crm-history">
            {contact.history.map((h, i) => {
              const Icon = KIND_ICON[h.kind] || MessageSquare;
              return (
                <li key={i} className="relative">
                  <span className="absolute -left-[31px] top-0 w-6 h-6 rounded-full bg-stone-900 border border-amber-500/40 flex items-center justify-center">
                    <Icon size={11} className="text-amber-400" />
                  </span>
                  <div className="paper !p-4">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="mono text-[10px] uppercase tracking-widest text-amber-500">
                        {t(`crm.confirm.kind.${h.kind}`)}
                      </div>
                      <div className="mono text-[10px] text-stone-500">{formatDate(h.ts, lang)}</div>
                    </div>
                    {h.note && <div className="text-stone-200 text-sm mt-1 whitespace-pre-line">{h.note}</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <ContactFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        contact={contact}
        onSaved={(c) => setContact(c)}
      />
      <ConfirmContactDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        contact={contact}
        onConfirmed={(c) => setContact(c)}
      />
    </div>
  );
}
